import { useCallback, useState } from "react";
import { FormState, UserInterfaceState } from "../../shared/Enum";

import { RoleEntity } from "../../api/Role/Entity/RoleEntity";
import {
  createRole,
  deleteRole,
  getRoleList,
  updateRole,
} from "../../api/Role/Repository";
import { debounce } from "@mui/material";
import { UpdateRoleDto } from "../../api/Role/Dto/UpdateRoleDto";
import { CreateRoleDto } from "../../api/Role/Dto/CreateRoleDto";
import { DeleteRoleDto } from "../../api/Role/Dto/DeleteRoleDto";
import { enqueueSnackbar } from "notistack";

interface Forms {
  onCreateRole: FormState;
  onDeleteRole: FormState;
  onUpdateRole: FormState;
}

interface Return {
  data: {
    uiState: UserInterfaceState;
    formState: Forms;
    roleList: RoleEntity[];
  };
  method: {
    onGetRoleList(filter?: string): void;
    onUpdateRole(payload: UpdateRoleDto): void;
    onCreateRole(payload: CreateRoleDto): void;
    onDeleteRole(payload: DeleteRoleDto): void;
  };
}

export default function useViewModel(): Return {
  const [uiState, setUiState] = useState<UserInterfaceState>(
    UserInterfaceState.Idle
  );
  const [formState, setFormState] = useState<Forms>({
    onCreateRole: FormState.Idle,
    onDeleteRole: FormState.Idle,
    onUpdateRole: FormState.Idle,
  });
  const [roleList, setRoleList] = useState<RoleEntity[]>([]);

  const onGetRoleList = useCallback(
    debounce(async (filter?: string) => {
      setUiState(UserInterfaceState.Loading);
      await getRoleList(filter)
        .then((res) => setRoleList(res))
        .then(() => setUiState(UserInterfaceState.Success))
        .catch((error) => {
          console.log("Error fetching data:", error);
          setUiState(UserInterfaceState.Error);
        });
    }, 300),
    [getRoleList]
  );

  const onUpdateRole = useCallback(
    async (payload: UpdateRoleDto) => {
      setFormState((prev) => ({ ...prev, onUpdateRole: FormState.Loading }));
      await updateRole(payload)
        .then(() => {
          setFormState((prev) => ({
            ...prev,
            onUpdateRole: FormState.Success,
          }));
          enqueueSnackbar("Role updated successfully.", { variant: "success" });
        })
        .then(() => onGetRoleList(""))
        .catch((error) => {
          console.log("Error fetching data:", error);
          setFormState((prev) => ({ ...prev, onUpdateRole: FormState.Error }));
        });
    },
    [updateRole]
  );

  const onCreateRole = useCallback(
    async (payload: CreateRoleDto) => {
      setFormState((prev) => ({ ...prev, onCreateRole: FormState.Loading }));
      await createRole(payload)
        .then(() => {
          setFormState((prev) => ({
            ...prev,
            onCreateRole: FormState.Success,
          }));
          enqueueSnackbar("Role created successfully.", { variant: "success" });
        })
        .then(() => onGetRoleList(""))
        .catch((error) => {
          console.log("Error fetching data:", error);
          setFormState((prev) => ({ ...prev, onCreateRole: FormState.Error }));
        });
    },
    [createRole]
  );

  const onDeleteRole = useCallback(
    async (payload: DeleteRoleDto) => {
      setFormState((prev) => ({ ...prev, onDeleteRole: FormState.Loading }));
      await deleteRole(payload)
        .then(() => {
          setFormState((prev) => ({
            ...prev,
            onDeleteRole: FormState.Success,
          }));
          enqueueSnackbar("Role deleted successfully.", { variant: "success" });
        })
        .then(() => onGetRoleList(""))
        .catch((error) => {
          console.log("Error fetching data:", error);
          setFormState((prev) => ({ ...prev, onDeleteRole: FormState.Error }));
        });
    },
    [deleteRole]
  );

  return {
    data: {
      uiState,
      formState,
      roleList,
    },
    method: {
      onGetRoleList,
      onUpdateRole,
      onCreateRole,
      onDeleteRole,
    },
  };
}
